import React, { useEffect, useState } from "react";
import { asyncGetReport } from "../../../utils/httpRequests";
import { useSelector } from "react-redux";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { ConcernType } from "../../../types/report.type";
import { useAlertContext } from "../../../contexts/alert/AlertProvider";
import { isArray } from "lodash";
import TableApp from "../../../components/common/tableapp/TableApp";
import { Box, IconButton, Stack, Typography } from "@mui/material";
import { HEADER_HEIGHT } from "../../../utils/ui.constants";
import { LuFilter } from "react-icons/lu";
import { IFilterReport } from "../../../types/filter.type";
import { useTheme } from "@mui/material/styles";
import { TableColumn } from "react-data-table-component";

interface IReportBase {
  report: {
    title: string;
    Filter: React.ElementType<IFilterReport>;
  };
}

const heights = {
  header: HEADER_HEIGHT,
  py: "32px",
  topList: "50px",
};
const widthes = {
  filter: "350px",
};

function ReportBase({ report }: IReportBase) {
  const { token } = useSelector((state: any) => state.auth);
  const { showAlert } = useAlertContext();
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any[]>([]);
  const [isChart, setIsChart] = useState<boolean>(false);
  const [queryObject, setQueryObject] = useState<any>({});
  const [concern, setConcern] = useState<ConcernType>();
  const [rowData, setRowData] = useState(null);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [expandFilter, setExpandFilter] = useState<boolean>(false);

  const getReport = async () => {
    try {
      setLoading(true);
      const resp = await asyncGetReport({
        token,
        apiCode: concern?.api as string,
        query: queryObject,
      });
      if (!resp || resp?.status !== 200) {
        showAlert({
          type: "error",
          message:
            resp?.data?.message || resp?.data?.error || "Lỗi khi tải báo cáo",
        });
      } else {
        const data: any[] = resp?.data || [];
        if (isArray(data)) {
          if (concern?.convertData) {
            const convertedData = concern.convertData(data, queryObject);
            setData(convertedData);
          } else {
            setData(data);
          }
        } else {
          showAlert({
            type: "error",
            message: resp?.message || resp?.error || "Lỗi khi tải báo cáo",
          });
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleExportReport = () => {
    // Chuyển đổi data-table thành mảng đối tượng
    const formattedData = data.map((row) => {
      const rowData: { [key: string]: any } = {};
      (concern?.columns || []).forEach((col: TableColumn<any>) => {
        rowData[col.name as string] = col?.selector?.(row);
      });
      return rowData;
    });
    // Tạo một work sheet từ dữ liệu đã định dạng
    const worksheet = XLSX.utils.json_to_sheet(formattedData);

    // Tạo một work book
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

    // Tạo buffer từ work book
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    // Tạo Blob từ buffer
    const dataBlob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
    });

    // Tự động tải xuống file
    saveAs(
      dataBlob,
      `${
        concern?.title || `Báo cáo${concern?.label ? ` ${concern.label}` : ""}`
      }.xlsx`
    );
  };

  useEffect(() => {
    setOpenModal(false);
    if (concern) {
      getReport();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [concern, queryObject]);

  return (
    <>
      {concern && concern.modal && openModal && (
        <concern.modal
          open={openModal}
          onClose={() => {
            setOpenModal(false);
          }}
          data={rowData}
          masterQueryObject={queryObject}
        />
      )}
      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          backgroundColor: "background.paper",
          borderRadius: "10px",
        }}
      >
        {/* Top */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            flexShrink: 0,
            height: heights.topList,
            px: 2,
            borderBottom: "1px dashed",
            borderColor: "divider",
          }}
        >
          <Typography
            sx={{ fontSize: "20px", lineHeight: "20px", fontWeight: 600 }}
          >
            {concern?.title || report?.title || "Báo cáo"}
          </Typography>
          <IconButton
            onClick={() => setExpandFilter(!expandFilter)}
            sx={{
              borderRadius: "10px",
              color: "common.white",
              backgroundColor: "secondary.main",
              "&:hover": {
                backgroundColor: "secondary.main",
              },
            }}
          >
            <LuFilter size={16} />
          </IconButton>
        </Stack>
        {/* Top end */}
        {/* Table */}
        <Box
          id="table-filter-layout"
          sx={{
            width: "100%",
            height: `calc(100vh - ${heights.header} - ${heights.py} - ${heights.topList} )`,
            display: "flex",
            flexDirection: "row",
            alignItems: "flex-start",
          }}
        >
          <Box
            sx={{
              width: `calc(100%${expandFilter ? ` - ${widthes.filter}` : ""})`,
              height: "100%",
              transition: "all 0.1s",
              transitionTimingFunction: "ease-out",
            }}
          >
            <TableApp
              columns={concern?.columns || []}
              progressPending={loading}
              data={data}
              onRowClicked={(row) => {
                setRowData(row);
                setOpenModal(true);
              }}
              fixedHeaderScrollHeight={`calc(100vh - ${heights.header} - ${heights.py} - ${heights.topList} )`}
            />
          </Box>
          {/* Filter */}
          <Box
            sx={{
              height: "100%",
              width: expandFilter ? widthes.filter : "0px",
              transition: "all 0.1s",
              transitionTimingFunction: "ease-out",
              borderLeft: "2px solid",
              borderColor: expandFilter ? "primary.main" : "transparent",
              overflow: "hidden",
              backgroundColor: "background.default",
            }}
          >
            <Box
              sx={{
                width: "100%",
                height: "100%",
              }}
            >
              {report.Filter && (
                <report.Filter
                  setQueryObject={setQueryObject}
                  setConcern={setConcern}
                />
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
}

export default ReportBase;
